import { useState, useRef } from 'react';
import { Upload, X, Loader2, FileIcon, Image, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

export default function AttachmentUpload({ annotationId, onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    // Vérifier la taille des fichiers (5 MB max)
    const maxSize = 5 * 1024 * 1024;
    const oversizedFiles = Array.from(files).filter(file => file.size > maxSize);

    if (oversizedFiles.length > 0) {
      toast.error(`Fichier(s) trop volumineux. Taille max: 5 MB`);
      return;
    }

    // Vérifier le nombre de fichiers (5 max)
    if (files.length > 5) {
      toast.error('Maximum 5 fichiers à la fois');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('attachments', file);
      });

      const response = await api.post(`/annotations/${annotationId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`${response.data.attachments.length} fichier(s) ajouté(s)`);

      // Notifier le composant parent
      if (onUploadComplete) {
        onUploadComplete(response.data.attachments);
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
        dragActive
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-300 hover:border-gray-400'
      }`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx"
        disabled={uploading}
      />

      <div className="text-center">
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            <p className="text-sm text-gray-600">Upload en cours...</p>
          </div>
        ) : (
          <>
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">
              Glissez-déposez des fichiers ici ou{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                parcourir
              </button>
            </p>
            <p className="text-xs text-gray-500">
              Images, PDF, Word, Excel • 5 fichiers max • 5 MB par fichier
            </p>
          </>
        )}
      </div>
    </div>
  );
}
